import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Skeleton, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Chip,
  TextField,
  Grid,
  Button,
  Alert,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { formatDate } from '../../utils/formatters';

interface KycUser {
  userId: number;
  username: string;
  emailHash?: string;
  status: string;
  kycStatus: string;
  createdAt: string;
  updatedAt?: string;
}

interface KycUsersResponse {
  data: KycUser[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const fetchKycUsers = async (page = 1, limit = 50, search = ''): Promise<KycUsersResponse> => {
  try {
    const params: any = { page, limit };
    if (search) params.search = search;

    const response = await api.get('/api/v1/admin-panel/kyc/users', {
      params,
      headers: {
        'x-hasura-admin-secret': localStorage.getItem('hasura_admin_secret') || 'admin_secret_key'
      }
    });

    return response.data.data;
  } catch (error) {
    console.error('Error fetching KYC users:', error);
    throw error;
  }
};

const KycUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<KycUser[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchKycUsers(1, 50, search);
      
      if (response?.data && Array.isArray(response.data)) {
        let filteredUsers = response.data;
        
        // Apply status filter on frontend since backend doesn't support it yet
        if (statusFilter !== 'all') {
          filteredUsers = response.data.filter(user => user.kycStatus === statusFilter);
        }
        
        setUsers(filteredUsers);
        setTotalUsers(response.total || 0);
      } else {
        console.error('Invalid response structure:', response);
        setUsers([]);
        setTotalUsers(0);
        setError('Не удалось загрузить пользователей: неверный формат данных');
      }
    } catch (err: any) {
      console.error('Error loading KYC users:', err);
      setUsers([]);
      setTotalUsers(0);
      setError(err.response?.data?.message || err.message || 'Ошибка при загрузке пользователей');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [search, statusFilter]);

  const handleRefresh = () => {
    loadUsers();
  };

  const handleViewUser = (userId: number) => {
    navigate(`/users/${userId}`);
  };

  const renderKycStatus = (status: string) => {
    switch (status) {
      case 'verified':
        return <Chip label="Верифицирован" color="success" size="small" />;
      case 'pending':
        return <Chip label="В ожидании" color="warning" size="small" />;
      case 'rejected':
        return <Chip label="Отклонен" color="error" size="small" />;
      case 'not_verified':
        return <Chip label="Не верифицирован" color="default" size="small" />;
      default:
        return <Chip label={status || 'Не определен'} color="default" size="small" />;
    }
  };

  const renderUserStatus = (status: string) => {
    switch (status) {
      case 'active':
        return <Chip label="Активен" color="success" size="small" />;
      case 'blocked':
        return <Chip label="Заблокирован" color="error" size="small" />;
      case 'suspended':
        return <Chip label="Приостановлен" color="warning" size="small" />;
      default:
        return <Chip label={status || 'Не определен'} color="default" size="small" />;
    }
  };

  const renderUsersTable = () => {
    if (loading) {
      return (
        <Box>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={60} sx={{ mb: 1 }} />
          ))}
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error">
          Ошибка при загрузке данных: {error}
        </Alert>
      );
    }

    if (!users || users.length === 0) {
      return (
        <Alert severity="info">
          Пользователи не найдены
        </Alert>
      );
    }

    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Имя пользователя</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell>Статус KYC</TableCell>
            <TableCell>Дата регистрации</TableCell>
            <TableCell>Действия</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.userId}>
              <TableCell>{user.userId}</TableCell>
              <TableCell>{user.username}</TableCell>
              <TableCell>{user.emailHash || '—'}</TableCell>
              <TableCell>{renderUserStatus(user.status)}</TableCell>
              <TableCell>{renderKycStatus(user.kycStatus)}</TableCell>
              <TableCell>{formatDate(user.createdAt)}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewUser(user.userId)}
                  >
                    Профиль
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Пользователи KYC
      </Typography>
      
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Поиск по ID, имени пользователя или email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Статус KYC</InputLabel>
              <Select
                value={statusFilter}
                label="Статус KYC"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Все статусы</MenuItem>
                <MenuItem value="not_verified">Не верифицирован</MenuItem>
                <MenuItem value="pending">В ожидании</MenuItem>
                <MenuItem value="verified">Верифицирован</MenuItem>
                <MenuItem value="rejected">Отклонен</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button 
              variant="contained" 
              onClick={handleRefresh} 
              size="small" 
              startIcon={<RefreshIcon />}
              fullWidth
            >
              Обновить
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Найдено пользователей: {users.length} из {totalUsers}
        </Typography>
        {renderUsersTable()}
      </Paper>
    </Box>
  );
};

export default KycUsersPage; 